import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { AddTitularDialog } from "@/components/AddTitularDialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { GerarDossierFinal } from "@/components/DossierFinalDialog";
import { PrepararEscritura } from "@/components/PrepararEscritura";
import { AngariacaoInfo } from "@/components/AngariacaoPanels";
import { PrepararVenda } from "@/components/PrepararVenda";
import { useMobile } from "@/components/ActionSheet";
import { NextStep } from "@/components/NextStep";
import { MoreMenu } from "@/components/MoreMenu";
import { openSpecial } from "@/components/SpecialSituations";
import { EstadoActions, ClosedBanner, DeedPrompt } from "@/components/EstadoActions";
import { EmailFailures } from "@/components/EmailFailures";
import { PendingBlock } from "@/components/PendingBlock";
import { gpTrata, mineOnly } from "@/components/gpView";
import { twilio } from "@/lib/whatsapp";
import { EscrituraCard, CreditCard, scrollToSec } from "@/components/DeedChecks";
import { DocGroups } from "@/components/DocGroups";
import { HistoryPanel } from "@/components/HistoryPanel";
import { ProcessSidebar, InboundAddress } from "@/components/ProcessSidebar";
import { CmiCard } from "@/components/CmiCard";
import { Fold } from "@/components/Fold";
import { ProcessHeader, GoalTags } from "@/components/ProcessHeader";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PortalLinks } from "@/components/PortalLinks";
import { EditarDados, Conflicts } from "@/components/EditarDados";
import { PartilhaComissao } from "@/components/PartilhaComissao";
import { PropostaAceite } from "@/components/PropostaAceiteDialog";
import { PassarDialog } from "@/components/GestaoDialogs";
import { DatesDialog } from "@/components/DatesDialog";
import { AnularProcesso } from "@/components/dossier/AnularProcesso";
import { DropZone } from "@/components/dossier/DropZone";
import { DocRow } from "@/components/dossier/DocRow";
import { DeedBlockers } from "@/components/dossier/DeedBlockers";
import { FocusDoc } from "@/components/dossier/FocusDoc";
import { ArrowLeft, Pencil, Calendar, Users } from "lucide-react";

// Fora do Dossier: se fosse criado a cada render, cada resposta voltava a montar o diálogo (lento e salta para o topo).
function Panel({ k, title, panel, setPanel, children }) {
  return (
    <Dialog open={panel === k} onOpenChange={(o) => !o && setPanel(null)}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid={`${k}-dialog`}>
        <DialogHeader><DialogTitle className="font-heading navy-text">{title}</DialogTitle></DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

const NEXT_LABEL = "Próxima ação";

// Cartão «Próxima ação»: o que fazer agora neste processo.
function NextActionCard({ children, label = NEXT_LABEL, testid = "next-step-card", light = false }) {
  return (
    <div className={`rounded-[18px] bg-white p-[18px] md:p-5 ${light ? "border border-slate-200" : "border-2 border-slate-900"}`} data-testid={testid}>
      <div className="text-xs font-bold tracking-wider text-slate-500 uppercase mb-3">{label}</div>
      {children}
    </div>
  );
}

export default function Dossier() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [p, setP] = useState(null);
  const [history, setHistory] = useState([]);
  const [focus, setFocus] = useState(null);
  const [links, setLinks] = useState([]);
  const [panel, setPanel] = useState(params.get("nova") === "1" ? "mensagens" : null);
  useEffect(() => { if (window.location.hash === "#cmi") { setPanel("cmi"); } }, []);

  const mobile = useMobile();
  const [estado, setEstado] = useState(null);
  const [anular, setAnular] = useState(false);
  const [passar, setPassar] = useState(false);
  const [datas, setDatas] = useState(false);
  const [uploadMany, setUploadMany] = useState(false);
  const [cpcvRead, setCpcvRead] = useState(null);
  const [docFilter, setDocFilter] = useState(null);
  const [histDoc, setHistDoc] = useState(null);

  const loadHistory = () => api.get(`/processes/${id}/history`).then(({ data }) => setHistory(data)).catch(() => {});
  const load = () => {
    loadHistory();
    api.get(`/processes/${id}/portal-links`).then(({ data }) => setLinks(data.links || [])).catch(() => {});
    return api.get(`/processes/${id}`).then(({ data }) => setP(data)).catch(() => navigate("/processos"));
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => () => { twilio.on = false; twilio.pid = null; }, []);
  const signing = p?.cmi_sign?.status === "enviado_assinaja";
  useEffect(() => {
    if (!signing) { return undefined; }
    const t = setInterval(load, 10000);
    return () => { clearInterval(t); };
  }, [signing, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const { hash, key: locKey } = useLocation();
  const handledHash = useRef(null);
  useEffect(() => {
    if (!p || !hash || handledHash.current === locKey) return;
    handledHash.current = locKey;
    const [kind, ...rest] = hash.slice(1).split("-");
    const key = rest.join("-");
    if (kind === "doc" && p.documents.some((d) => d.key === key)) setTimeout(() => setFocus({ key, t: Date.now() }), 300);
    else if (kind === "sec" && ["cpcv", "escritura", "credito"].includes(key)) setTimeout(() => scrollToSec(key), 400);
    else if (kind === "sec" && key === "pedir") askDocs();
    else if (kind === "sec" && ["situacoes", "respostas"].includes(key)) setTimeout(() => openSpecial(), 300);
    else if (kind === "sec" && ["cmi", "conflitos", "mensagens", "historico"].includes(key)) setPanel(key);
    else if (kind === "add" && key === "titular") setPanel("add-titular");
  }, [p, id, hash, locKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const f = () => load();
    window.addEventListener("process-changed", f);
    return () => window.removeEventListener("process-changed", f);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveDeed = async (val, extra = {}) => {
    await api.put(`/processes/${id}`, { ...(val ? { deed_date: val } : {}), ...extra });
    toast.success(val ? "Data da escritura atualizada" : "Escritura atualizada");
    load();
  };

  const calAdded = () => api.post(`/processes/${id}/calendar/added`).then(load);
  const downloadIcs = async () => {
    const res = await api.get(`/processes/${id}/calendar.ics`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "text/calendar" }));
    const a = document.createElement("a"); a.href = url; a.download = `escritura-${p.ref}.ics`; a.click();
    URL.revokeObjectURL(url); load();
  };

  if (!p) return <div className="text-slate-400 py-20 text-center">A carregar…</div>;

  const ang = p.stage === "angariacao";
  const missing = p.status !== "escriturado" && p.documents.some((d) => !d.optional && d.status !== "nao_aplica" && !["validado", "na"].includes(d.verdict));
  const groupLabels = {
    vendedor: `${(p.sellers || []).length > 1 ? "Proprietários" : "Proprietário"}${p.stage !== "angariacao" ? " (vendedor)" : ""}`,
    comprador: (p.buyers || []).length > 1 ? "Compradores" : "Comprador",
  };
  const focusDoc = (key) => setFocus({ key, t: Date.now() });
  const ro = user?.role === "direccao" || (user?.role === "gestor" && ang);
  const canAnular = user?.role === "gestor" || (user?.role === "consultor" && p.status !== "escriturado");
  const closed = ["escriturado", "sem_venda"].includes(p.status);
  const nNeed = p.missing_docs?.required ?? (p.documents || []).filter((d) => !d.optional && d.status !== "nao_aplica").length;
  const nOk = p.missing_docs?.ready ?? (p.documents || []).filter((d) => !d.optional && d.status !== "nao_aplica" && d.verdict === "validado").length;
  const nFix = p.missing_docs?.corrigir?.n || 0;
  const rb = p.review_by || "ambos";
  const nRev = (rb === "ambos" || rb === (user?.role === "consultor" ? "consultor" : "gestao")) ? p.missing_docs?.rever?.n || 0 : 0;
  const menu = {
    title: [p.card?.num, p.card?.address].filter(Boolean).join(" · ") || p.ref,
    cal: p.calendar ? { google: () => { window.open(p.calendar.google_url, "_blank", "noopener"); calAdded(); }, ics: downloadIcs } : null,
    historico: () => setPanel("historico"), historyCount: history.length,
    mensagens: closed ? null : () => setPanel("mensagens"),
    escritura: !ang && !closed && !ro ? () => setEstado("escriturado") : null,
    semVenda: !closed && !ro ? () => setEstado("sem_venda") : null,
    anular: canAnular ? () => setAnular(true) : null,
    panels: [
      !ro && { label: "Editar dados do processo", icon: Pencil, onClick: () => setPanel("editar"), testid: "more-editar" },
      !ro && !ang && !closed && { label: "Alterar datas (CPCV e escritura)", icon: Calendar, onClick: () => setDatas(true), testid: "more-datas" },
      user?.role === "gestor" && p.gestao?.id === user?.id && !closed && { label: "Passar a um colega", icon: Users, onClick: () => setPassar(true), testid: "more-passar" },
    ].filter(Boolean),
  };
  const docs = docFilter ? p.documents.filter((d) => docFilter.keys.has(d.key)) : p.documents;
  const toDocs = () => document.querySelector("[data-testid=documents-card]")?.scrollIntoView({ behavior: "smooth", block: "start" });
  function askDocs() {
    setPanel(null);
    setTimeout(() => {
      const el = document.querySelector("[data-remind=vendedor]");
      if (!el) { toDocs(); return; }
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.click();
    }, 400);
  }
  const deedReady = !ang && !closed && !!p.closing?.ready;
  const nValid = p.documents.filter((d) => d.verdict === "validado").length;
  const banner = ro ? null : p.next_step?.key === "gerar_cmi" && p.next_step.enabled ? "Tem todos os dados para o contrato de mediação"
    : deedReady ? `Tudo pronto para a escritura: ${nValid} ${nValid === 1 ? "documento válido" : "documentos válidos"}` : null;
  const nextStep = <NextStep p={p} onDone={load} onPanel={setPanel} onFocus={(k) => { setDocFilter(null); focusDoc(k); }} onUpload={() => { setUploadMany(true); setTimeout(toDocs, 50); }}
    onSaveDeed={saveDeed} preload={cpcvRead} onPreloadUsed={() => setCpcvRead(null)} />;
  // Estados do processo («registar proposta», «à espera de proposta») nunca são a próxima ação principal.
  const stateStep = ["aguardar_proposta", "proposta"].includes(p.next_step?.key) || (p.next_step?.key === "marcar_escritura" && !gpTrata(p));
  const status = !ro && p.next_step?.key === "aguardar_proposta" ? { label: p.next_step.label, sub: p.next_step.sub } : null;
  const side = (
    <>
      {!ro && !ang && !mobile && p.next_step && !stateStep && <div data-testid="header-next-action"><div className="text-[11px] font-bold tracking-wider text-slate-500 uppercase mb-1.5">{NEXT_LABEL}</div>{nextStep}</div>}
      {!ro && deedReady && <PrepararEscritura p={p} onChange={load} />}
      <div className="flex flex-wrap gap-2" data-testid="process-top-actions">
        {["gerado", "enviado", "lembrar"].includes(p.cmi_state) && <button onClick={() => setPanel("cmi")} data-testid="top-cmi-btn"
          className="inline-flex items-center justify-center px-5 h-12 md:h-11 rounded-[14px] border border-slate-300 bg-white text-[15px] font-semibold navy-text hover:bg-slate-50 transition-colors">Ver CMI</button>}
        {!ro && ((ang && p.status === "angariacao" && p.mediation?.signed_date) || (!ang && p.next_step?.key === "proposta")) && (
          <PropostaAceite pid={id} p={p} askingPrice={p.mediation?.asking_price} onDone={load} label="Registar proposta aceite"
            className="flex-1 inline-flex items-center justify-center px-5 h-12 md:h-11 rounded-[14px] border-2 border-ec-green bg-white text-[15px] font-semibold text-ec-green hover:bg-ec-green hover:text-white transition-colors" />
        )}
        <div className="flex-1 min-w-[110px]"><MoreMenu menu={menu} block /></div>
      </div>
      {!ang && !(deedReady && !ro) && <GerarDossierFinal pid={id} ref_={p.ref} ready={!!p.closing?.ready} provisional={missing || !!p.closing?.final_blocker} blocker={p.closing?.final_blocker} onChange={load} block />}
    </>
  );
  const pp = { panel, setPanel };
  // Bloqueios e pendentes vivem só em «O que falta para a escritura»: sem chip a repeti-los no topo.
  const inBlock = !ang && !closed && ((p.todo?.deed || []).length > 0 || (p.a_tratar?.lines || []).length > 0);
  // Angariação: o próximo passo do CMI sai do topo; se não for pedir à pessoa (cartão dela), é o cartão «Próxima ação».
  const personCard = (p.a_tratar?.lines || []).some((l) => l.id === "lembrar-vendedor");
  const nsCard = ang && !ro && !closed && p.next_step && !stateStep && !(p.next_step.key === "pedir_docs" && personCard) ? (
    <NextActionCard><div className="md:max-w-sm">{nextStep}</div></NextActionCard>
  ) : null;
  // Telemóvel: a única ação grande logo a seguir ao título.
  const advance = !ro && p.next_step && !stateStep && <NextActionCard label={NEXT_LABEL} testid="advance-card">{nextStep}</NextActionCard>;
  const lead = !mobile ? null : ang ? nsCard : advance;
  twilio.pid = id; twilio.on = !!p.twilio_wa;
  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/processos")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-ec-green transition-colors" data-testid="back-btn">
        <ArrowLeft size={16} /> {user?.role === "direccao" ? "Visão da agência" : "Processos"}
      </button>
      <ProcessHeader p={p} onSaveDeed={ro || gpTrata(p) ? null : saveDeed} menu={menu} side={side} banner={banner} lead={lead}
        hideSummary={!ang && !closed && (p.todo?.deed || []).length > 0}
        tags={inBlock ? null : <GoalTags p={p} filter={docFilter} setFilter={(f) => { setDocFilter(f); if (f) setTimeout(toDocs, 50); }} />} />
      <EstadoActions p={p} onDone={load} open={estado} setOpen={setEstado} />
      <PassarDialog pid={id} open={passar} onClose={() => setPassar(false)} onDone={load} />
      {datas && <DatesDialog p={p} open onClose={() => setDatas(false)} onSaved={() => { setDatas(false); load(); }} />}
      {canAnular && <AnularProcesso pid={id} ref_={p.ref} onDeleted={() => navigate("/processos")} open={anular} setOpen={setAnular} />}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-8 space-y-3 md:space-y-6 min-w-0">
          <ClosedBanner p={p} />
          <EmailFailures pid={id} items={p.email_failures} onDone={load} ro={ro} />
          <DeedPrompt p={p} onDone={load} ro={ro} trata={gpTrata(p)} onConfirm={ro ? null : () => setEstado("escriturado")} />
          {(p.infos || []).filter((i) => !(gpTrata(p) && i.id === "kyc_cpcv")).map((i) => (
            <div key={i.id} className="rounded-xl border border-sky-200 bg-sky-50 px-5 py-3 text-sm text-sky-900" data-testid={`info-${i.id}`}>{i.text}</div>
          ))}
          {!closed ? <PendingBlock pid={id} p={gpTrata(p) ? mineOnly(p) : p} links={links} onFocus={focusDoc} onChange={load} head={mobile ? null : nsCard} flat={!ang} status={status} between={<DeedBlockers p={p} />} /> : <DeedBlockers p={p} />}
          <FocusDoc p={p} ro={ro} onPanel={setPanel} onDone={load} onFocus={(k) => { setDocFilter(null); focusDoc(k); }} />

          <Fold title={["escriturado", "sem_venda"].includes(p.status) ? "Arquivo do processo" : "Dossier documental"} testid="documents-card" memo="docs" defaultOpen={false} open={!!focus || /^#doc-/.test(window.location.hash)}
            sub={`${nOk}/${nNeed} OK${p.missing_docs?.total ? ` · ${p.missing_docs.total}${closed ? " não ficaram no arquivo" : " em falta"}` : ""}${nFix ? ` · ${nFix} a corrigir` : ""}${nRev ? ` · ${nRev} por rever` : ""}`}>
            <div className="flex items-center gap-3 px-6 pb-2">
              <span className="mr-auto" data-testid="documents-title" />
              {docFilter && <span className="text-sm text-slate-500" data-testid="doc-filter">Filtro: {docFilter.label} · {docs.length} em falta · <button onClick={() => setDocFilter(null)} className="underline" data-testid="doc-filter-clear">limpar</button></span>}
              {!ro && !gpTrata(p) && <button onClick={() => setUploadMany(!uploadMany)} data-testid="upload-many-btn"
                className="text-[15px] font-semibold navy-text px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">+ Carregar</button>}
            </div>
            {uploadMany && <div className="px-6 pb-2"><DropZone pid={id} onDone={load} onCpcv={setCpcvRead} /></div>}
            <DocGroups pid={id} p={p} docs={docs} onChange={load} focus={focus} Detail={DocRow} labels={groupLabels} links={links} onHistory={(d) => { setHistDoc(d.name); setPanel("historico"); }} />
            {!closed && <InboundAddress pid={id} />}
          </Fold>
          {ang && <PrepararVenda pid={id} ro={ro} />}
          {ang && <AngariacaoInfo p={p} onDone={ro ? null : load} />}
          <PartilhaComissao p={p} onDone={load} />
          <p className="text-xs text-slate-400" data-testid="legal-notice">A ferramenta organiza e assinala divergências; não dá aconselhamento jurídico nem substitui a análise de um advogado ou solicitador.</p>
        </div>

        <div className="lg:col-span-4 space-y-4 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:pb-2 lg:-mr-2 lg:pr-2" data-testid="process-side-col">
          <ProcessSidebar p={p} links={links} onDone={load} extra={!ang && !gpTrata(p) && <Fold title="Escritura e crédito" testid="credit-fold" open={/^#sec-(credito|escritura)/.test(window.location.hash)}>
            <div className="space-y-3 px-2 md:px-0">
              <EscrituraCard p={p} onDone={load} />
              <CreditCard p={p} onDone={load} />
            </div>
          </Fold>} />
        </div>
      </div>

      <Panel panel={panel === "cmi-sign" ? "cmi" : panel} setPanel={setPanel} k="cmi" title="Contrato de mediação"><CmiCard pid={id} p={p} readOnly={ro} showSteps={panel === "cmi-sign"} onAsk={askDocs} key={`cmi-${p.updated_at || ""}-${(p.documents || []).length}`} /></Panel>
      <Panel {...pp} k="conflitos" title="Respostas em conflito"><Conflicts p={p} onDone={load} /></Panel>
      <Panel {...pp} k="editar" title="Editar dados"><EditarDados p={p} onDone={() => { setPanel(null); load(); }} /></Panel>
      <Panel {...pp} k="proposta" title="Proposta aceite"><NextStep p={{ ...p, next_step: { key: "proposta" } }} onDone={() => { setPanel(null); load(); }} onPanel={setPanel} /></Panel>
      {panel === "add-titular" && <AddTitularDialog p={p} open onClose={() => setPanel(null)} onDone={() => { setPanel(null); load(); }} />}

      <Dialog open={panel === "mensagens" && !closed} onOpenChange={(o) => !o && setPanel(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="mensagens-dialog">
          <DialogHeader><DialogTitle className="font-heading text-ec-green">Página do cliente</DialogTitle></DialogHeader>
          <div className="space-y-6">
            <PortalLinks pid={id} highlight={params.get("nova") === "1"} />
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={panel === "historico"} onOpenChange={(o) => { if (!o) { setPanel(null); setHistDoc(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="historico-dialog">
          <DialogHeader><DialogTitle className="font-heading text-ec-green">Histórico{histDoc ? ` · ${histDoc}` : ""}</DialogTitle></DialogHeader>
          {histDoc && <button onClick={() => setHistDoc(null)} className="text-sm underline text-slate-500 self-start" data-testid="history-doc-clear">Ver o histórico todo</button>}
          <HistoryPanel key={histDoc || "all"} events={histDoc ? history.filter((e) => `${e.title} ${e.detail || ""}`.includes(histDoc)) : history} initialFilter={histDoc ? "todos" : undefined} defaultOpen bare />
        </DialogContent>
      </Dialog>
    </div>
  );
}
